<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { ProfileAdministrationAPI } from "../api";
import { CustomerPurchaseProjection, DistributorPurchaseProjection } from "../types/PersologOnlineTypes.ts";
import { refArray } from "../main.ts";
import { FilterMatchMode } from "@primevue/core/api";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const dates = ref();
const customerPurchases = refArray<CustomerPurchaseProjection>();
const distributorPurchases = refArray<DistributorPurchaseProjection>();
const totalQuantity = ref(0);
const dtCustomers = ref();
const dtDistributors = ref();

const customerFilters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    firstname: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    lastname: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    productProduct: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    distributorCompany: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
});

const distributorFilters = ref({
    company: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    productProduct: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
});

async function loadData() {
    if (!dates.value || dates.value.length !== 2 || !dates.value[0] || !dates.value[1]) {
        return;
    }
    const startDate = formatDate(dates.value[0]);
    const endDate = formatDate(new Date(dates.value[1].setHours(23, 59, 59, 999)));

    customerPurchases.value = await ProfileAdministrationAPI.fetchCustomerPurchases(startDate, endDate);
    distributorPurchases.value = await ProfileAdministrationAPI.fetchDistributorPurchases(startDate, endDate);

    totalQuantity.value = distributorPurchases.value.reduce((sum, purchase) => sum + purchase.quantity, 0);
}

function formatDate(date: Date): string {
    return date ? date.toISOString().replace("T", " ").replace("Z", "") : "";
}

const exportCustomers = (event: Event) => {
    dtCustomers.value.exportCSV(event);
};

const exportDistributors = (event: Event) => {
    dtDistributors.value.exportCSV(event);
};

function buildFileName(type: string): string {
    if (!dates.value || dates.value.length !== 2 || !dates.value[0] || !dates.value[1]) {
        return `${type}-purchases`;
    }
    const startDate = `${dates.value[0].getFullYear()}-${dates.value[0].getMonth() + 1}-${dates.value[0].getDate()}`;
    const endDate = `${dates.value[1].getFullYear()}-${dates.value[1].getMonth() + 1}-${dates.value[1].getDate()}`;
    return `${startDate}_${endDate}_${type}-purchases`;
}

watch(
    () => dates.value,
    () => {
        loadData();
    },
);

onMounted(async () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0); // 0 means "last day of previous month"

    dates.value = [firstDay, lastDay];

    await loadData();
});
</script>

<template>
    <h1 class="page-title">{{ t("billing.title") }}</h1>
    <DatePicker
        id="billing-view_date-picker_range"
        class="mt-8"
        v-model="dates"
        selectionMode="range"
        :manualInput="false"
        :numberOfMonths="2"
        showIcon
        fluid
        inline
        showButtonBar
    />

    <h2 class="mt-6">{{ t("billing.customer") }}</h2>
    <DataTable
        id="billing-view_table_customer-purchases"
        v-model:filters="customerFilters"
        filterDisplay="menu"
        class="mt-1"
        ref="dtCustomers"
        :value="customerPurchases"
        tableStyle="min-width: 50rem"
        :paginator="true"
        :rows="5"
        csvSeparator=";"
        :exportFilename="buildFileName('customer')"
        :globalFilterFields="['email', 'firstname', 'lastname', 'productProduct', 'distributorCompany']"
    >
        <template #header>
            <div class="flex justify-end">
                <IconField>
                    <InputIcon>
                        <i class="pi pi-search" />
                    </InputIcon>
                    <InputText id="billing-view_input_search" v-model="customerFilters['global'].value" :placeholder="t('buttons.find')" />
                </IconField>
            </div>
        </template>
        <template #empty>{{ t("billing.empty") }}</template>
        <Column field="email" :header="t('keycloak.email')" />
        <Column field="firstname" :header="t('keycloak.firstname')" />
        <Column field="lastname" :header="t('keycloak.lastname')" />
        <Column field="productProduct" :header="t('projects.product')">
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()" :placeholder="t('buttons.find')" />
            </template>
        </Column>
        <Column field="quantity" sortable :header="t('trainer.quantity.label')" />
        <Column field="distributorCompany" :header="t('trainer.distributor.label')">
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()" :placeholder="t('buttons.find')" />
            </template>
        </Column>
        <template #paginatorstart>
            <Tag id="billing-view_tag_customer">{{ t("billing.total.quantity") }} {{ totalQuantity }}</Tag>
        </template>
        <template #paginatorend>
            <Button
                icon="pi pi-download"
                text
                :label="t('buttons.export')"
                size="small"
                @click="exportCustomers($event)"
                :disabled="customerPurchases.length == 0"
            />
        </template>
    </DataTable>

    <h2 class="mt-20">{{ t("billing.distributor") }}</h2>
    <DataTable
        id="billing-view_table_distributor-purchases"
        v-model:filters="distributorFilters"
        filterDisplay="row"
        class="mt-1"
        ref="dtDistributors"
        :value="distributorPurchases"
        tableStyle="min-width: 50rem"
        :paginator="true"
        :rows="5"
        csvSeparator=";"
        :exportFilename="buildFileName('distributor')"
    >
        <template #empty>{{ t("billing.empty") }}</template>
        <Column field="company" :header="t('trainer.distributor.label')">
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()" :placeholder="t('buttons.find')" />
            </template>
        </Column>
        <Column field="productProduct" :header="t('projects.product')">
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()" :placeholder="t('buttons.find')" />
            </template>
        </Column>
        <Column field="quantity" sortable :header="t('trainer.quantity.label')" />
        <template #paginatorstart>
            <Tag id="billing-view_tag_distributor">{{ t("billing.total.quantity") }} {{ totalQuantity }}</Tag>
        </template>
        <template #paginatorend>
            <Button
                icon="pi pi-download"
                text
                :label="t('buttons.export')"
                size="small"
                @click="exportDistributors($event)"
                :disabled="distributorPurchases.length == 0"
            />
        </template>
    </DataTable>
</template>
